// Types and display rules for the Marketing tab.
//
// A plain .ts module with no JSX and no imports beyond types, so Node's test
// runner can check it under --experimental-strip-types. The things that are easy
// to get wrong here are the reading order of the board and what each state is
// called on screen, and neither shows up in a typecheck.
//
// NOTHING IS CALCULATED HERE. Every figure on the screen comes from
// public.v_content_board and public.v_design_request_board, per CLAUDE.md rule
// 4. This file chooses words and an order for facts the server has already
// decided.

export type ContentState =
  | 'Draft'
  | 'Submitted'
  | 'Changes requested'
  | 'Approved'
  | 'Published'
  | 'Archived'

export type ContentAsset = {
  id: string
  filename: string
  mime_type: string | null
  storage_path: string
  version: number
  size_bytes: string | number | null
  uploaded_at: string
  is_image: boolean
  is_video: boolean
  // True when this file came in against a design request and is being used as
  // the content it was made for. The request keeps it too.
  from_design_request?: boolean
  // Reference is what the person asking sent in to show the designer what they
  // meant; Artwork is the work. Only Artwork counts as a delivery and only
  // Artwork can go out as content. Optional because v_content_board's rows
  // predate the column and are all Artwork by definition.
  kind?: 'Reference' | 'Artwork'
}

/** One candidate caption. Several per content item; one of them goes out. */
export type CaptionOption = {
  id: string
  label: string
  body: string
  cta: string | null
  hashtags: string[]
  updated_at: string
}

/**
 * A production-note guardrail the selected caption trips.
 *
 * Advisory. The scan never refuses a write, and the message quotes the plan back
 * rather than asserting a rule of its own. What it does do is make approving
 * without having read it impossible: content_decide_v1 refuses an approval whose
 * flags have not each been acknowledged by code.
 */
export type GuardrailFlag = {
  code: string
  label: string
  message: string
  source_note: string
  excerpt: string | null
}

export type GuardrailRule = {
  code: string
  label: string
  audience: string | null
  message: string
  source_note: string
}

/** Planned share against delivered share, per audience, counted in posts. */
export type PlanMixRow = {
  audience: string
  audience_label: string | null
  target_share: number | null
  actual_share: number | null
  posts: number
  target_posts: number | null
  posts_gap: number | null
}

export type ContentRow = {
  id: string
  ref: number
  plan_id: string | null
  plan_title: string | null
  plan_month: string | null
  post_no: number | null
  title: string
  audience: string
  audience_label: string | null
  channel: string
  channel_label: string | null
  format: string
  format_label: string | null
  cta: string | null
  // The words that go ON the artwork. A different piece of writing from the
  // caption, by a different person: Turf sets this inside the image.
  design_copy: string | null
  creative_direction: string | null
  // Which approved document the figures came from. A number on a driver-facing
  // post with no source is a number nobody can defend.
  source_basis: string | null
  // The guardrail in the plan's own words, carried on the row so it is in front
  // of both the writer and the approver.
  production_note: string | null
  hashtags: string[]
  // The SELECTED option's words, or null when nothing has been chosen. Null is a
  // real state and not an empty string.
  caption: string | null
  caption_label: string | null
  selected_caption_id: string | null
  caption_option_count: number
  caption_options: CaptionOption[]
  guardrail_flags: GuardrailFlag[]
  guardrail_flag_count: number
  caption_not_chosen: boolean
  figure_with_no_source: boolean
  brief: string | null
  scheduled_for: string | null
  published_at: string | null
  state: ContentState
  task_id: string | null
  task_ref: number | null
  task_status: string | null
  submitted_by: string | null
  submitted_at: string | null
  decided_by: string | null
  decided_at: string | null
  created_by: string | null
  created_at: string
  asset_count: number
  asset_version: number | null
  last_upload_at: string | null
  assets: ContentAsset[]
  changes_requested_count: number
  submission_count: number
  last_submitted_at: string | null
  last_decision_at: string | null
  last_decision: string | null
  last_decision_note: string | null
  last_decision_by: string | null
  awaiting_approval: boolean
  days_waiting: number | null
  approved_and_overdue: boolean
  submitted_with_no_file: boolean
}

export type ContentReview = {
  id: string
  decision: 'Submitted' | 'Approved' | 'Changes requested' | 'Withdrawn' | 'Reopened'
  note: string | null
  asset_count: number
  asset_ids: string[]
  // The words that were actually approved. The id alone is not enough: an option
  // can be rewritten after it was sent back, and an approval that can no longer
  // show the sentence it approved is not a record.
  selected_caption_id: string | null
  caption_snapshot: string | null
  guardrail_flags: GuardrailFlag[]
  acknowledged_codes: string[]
  from_state: string
  to_state: string
  actor_email: string | null
  actor_name: string | null
  at: string
}

/** One edit to a post, from public.content_item_history (migration 0410). */
export type ContentHistoryRow = {
  id: string
  field: string
  old_value: string | null
  new_value: string | null
  actor_email: string | null
  actor_name: string | null
  at: string
}

const HISTORY_LABELS: Record<string, string> = {
  title: 'Title',
  audience: 'Audience',
  channel: 'Channel',
  format: 'Format',
  post_no: 'Post number',
  design_copy: 'Design copy',
  cta: 'Call to action',
  creative_direction: 'Creative direction',
  source_basis: 'Figures come from',
  production_note: 'Production note',
  hashtags: 'Hashtags',
  brief: 'Brief',
  scheduled_for: 'Goes out on',
  published_at: 'Went out on',
  state: 'State',
}

/** The field as a person would name it. Caption rows already read that way. */
export function historyFieldLabel(field: string): string {
  const known = HISTORY_LABELS[field]
  if (known) return known
  return field.charAt(0).toUpperCase() + field.slice(1)
}

/**
 * A stored value as it should read. Arrays arrive as JSON text from the
 * history trigger, which reads rows through to_jsonb; an empty value is said,
 * not left blank, because a blank reads as a value nobody recorded.
 */
export function historyValue(v: string | null): string {
  if (v === null || v === '') return 'empty'
  if (v.startsWith('[') && v.endsWith(']')) {
    try {
      const arr = JSON.parse(v) as unknown
      if (Array.isArray(arr)) return arr.length === 0 ? 'empty' : arr.join(' ')
    } catch {
      return v
    }
  }
  return v
}

export type DesignRequestRow = {
  id: string
  ref: number
  title: string
  brief: string
  channel: string | null
  channel_label: string | null
  format: string | null
  format_label: string | null
  dimensions: string | null
  reference_urls: string[] | null
  needed_by: string | null
  priority: string | null
  status: 'Requested' | 'In progress' | 'Delivered' | 'Cancelled'
  content_item_id: string | null
  content_item_title: string | null
  task_id: string | null
  task_ref: number | null
  task_status: string | null
  requested_by: string | null
  requested_at: string
  assigned_to: string | null
  assigned_to_name: string | null
  delivered_at: string | null
  // ARTWORK ONLY, both of them. Every reader of asset_count is asking whether
  // the work has been done, so a reference sent in with the request must not
  // move it. See migration 0118.
  asset_count: number
  assets: ContentAsset[]
  reference_count: number
  reference_assets: ContentAsset[]
  overdue: boolean
  days_to_needed: number | null
  delivered_with_no_file: boolean
}

export type MarketingPlan = {
  id: string
  period_month: string
  title: string
  objective: string | null
  status: 'Draft' | 'Active' | 'Closed'
  // How many posts the month is planned around. Every share is a share of this,
  // so a percentage on screen without it is a percentage nobody can check.
  feed_size: number | null
  canvas_default: string | null
  principles: string | null
  item_count: number
  approved_count: number
  awaiting_count: number
  published_count: number
  published_pct: number | null
  // The mix, counted in POSTS. A twenty-post feed moves five percentage points
  // per post, so a percentage gap cannot express a one-post error and a one-post
  // error is the only kind anybody makes.
  worst_gap: number | null
  mix_summary: string | null
  mix_off_target: boolean
  mix: PlanMixRow[]
}

export type MarketingPerson = {
  email: string
  name: string
  can_edit_marketing: boolean
  can_approve_content: boolean
  is_designer: boolean
}

export type CodeLabel = { code: string; label: string }

export type MarketingRefs = {
  channels: CodeLabel[]
  formats: CodeLabel[]
  audiences: CodeLabel[]
  ctas: CodeLabel[]
  guardrails: GuardrailRule[]
  plans: MarketingPlan[]
  people: MarketingPerson[]
  canEditMarketing: boolean
  canApproveContent: boolean
  isDesigner: boolean
}

// ---------------------------------------------------------------------------
// Reading order
// ---------------------------------------------------------------------------
// Waiting on the approver reads first, because it is the only queue in this
// domain and it is the one where the delay costs somebody else their day.
// Changes requested reads second: the work is back with its author and needs
// them, so it is the second most actionable thing on the screen. Draft is where
// work in progress lives and reads third.
//
// Published and Archived read last and are hidden by default. Two hundred
// published posts would bury the six items that need somebody today.
export const STATE_ORDER: ContentState[] = [
  'Submitted',
  'Changes requested',
  'Draft',
  'Approved',
  'Published',
  'Archived',
]

// What each state is called at the top of its group. The names say who is
// holding the work, not what the row's status field contains, because "who has
// this" is the question a person opening this screen actually has.
export const STATE_HEADINGS: Record<ContentState, string> = {
  Submitted: 'Waiting on approval',
  'Changes requested': 'Back with the author',
  Draft: 'Being drafted',
  Approved: 'Approved, ready to go out',
  Published: 'Published',
  Archived: 'Archived',
}

export const STATE_TONES: Record<ContentState, 'amber' | 'red' | 'neutral' | 'green' | 'brand'> = {
  Submitted: 'amber',
  'Changes requested': 'red',
  Draft: 'neutral',
  Approved: 'brand',
  Published: 'green',
  Archived: 'neutral',
}

export type ContentGroup = { state: ContentState; heading: string; rows: ContentRow[] }

/**
 * Groups the board by state in reading order, dropping groups with nothing in
 * them.
 *
 * An empty group is dropped rather than shown, deliberately: a screen with six
 * headings and two rows reads as broken. The counts strip above the board is
 * where the zeroes belong, because there a zero is information.
 */
export function groupByState(rows: ContentRow[]): ContentGroup[] {
  const out: ContentGroup[] = []
  for (const state of STATE_ORDER) {
    const inState = rows.filter((r) => r.state === state)
    if (inState.length === 0) continue
    out.push({ state, heading: STATE_HEADINGS[state], rows: inState })
  }
  return out
}

/** The counts strip. Zeroes are kept: here a zero says something. */
export function boardCounts(rows: ContentRow[]) {
  return {
    total: rows.length,
    awaiting: rows.filter((r) => r.awaiting_approval).length,
    // The one figure on this screen that is a complaint rather than a count.
    // Approved, dated, the date has passed, and nothing recorded as published.
    overdue: rows.filter((r) => r.approved_and_overdue).length,
    noFile: rows.filter((r) => r.submitted_with_no_file).length,
    // Named for what it is. Content with no date is not scheduled, it is
    // unmeasured, and calling it anything softer hides the gap.
    noDate: rows.filter((r) => r.state !== 'Published' && r.scheduled_for === null).length,
    // Options written and none picked. The commonest way a submission wastes a
    // round, and the reason the database refuses to submit in that state.
    notChosen: rows.filter((r) => r.caption_not_chosen).length,
    // Captions that trip a production-note guardrail. Not an error: it is a
    // second look, and the count is here so the second look can be planned
    // rather than discovered one row at a time.
    flagged: rows.filter((r) => r.guardrail_flag_count > 0).length,
    // A figure in the caption and no source document named. The plan says four
    // separate times to reproduce approved figures exactly, which is not
    // checkable at all if nobody wrote down where they came from.
    noSource: rows.filter((r) => r.figure_with_no_source).length,
  }
}

/**
 * Whether every flag on a row has been ticked.
 *
 * The gate is enforced in Postgres; this is only so the button can say what it
 * is waiting for instead of refusing after the click.
 */
export function allFlagsAcknowledged(row: ContentRow, acknowledged: string[]): boolean {
  return (row.guardrail_flags ?? []).every((f) => acknowledged.includes(f.code))
}

/** "Driver 17 of 16 planned" reads without interpretation; a percentage does not. */
export function mixGapLabel(row: PlanMixRow): string | null {
  if (row.posts_gap === null || row.posts_gap === 0) return null
  const n = Math.abs(row.posts_gap)
  const word = n === 1 ? 'post' : 'posts'
  return row.posts_gap > 0 ? `${n} ${word} over` : `${n} ${word} short`
}

/**
 * The longest anything has been sitting with the approver, or null when nothing
 * is waiting.
 *
 * Null and not zero. Nothing waiting is not the same as something that has been
 * waiting no time at all, and this number is shown to the approver.
 */
export function longestWait(rows: ContentRow[]): number | null {
  const waits = rows
    .filter((r) => r.awaiting_approval && r.days_waiting !== null)
    .map((r) => r.days_waiting as number)
  return waits.length === 0 ? null : Math.max(...waits)
}

/**
 * What can be done to a row, from its state alone.
 *
 * Derived in one place so a button and the refusal behind it cannot disagree.
 * Every one of these is also enforced by the database, which is what actually
 * holds; this only decides what to offer.
 */
/**
 * The fields an approval covers. THE MIRROR OF content_approved_fields_v1 in
 * db/migrations/0410, pinned against it by web/test/marketing.test.ts. Changing
 * one of these on an approved post sends it back to Draft, so the screen warns
 * before it saves. Everything else on a post (date, brief, creative direction,
 * source, production note, post number, plan) is a note to the team and stays
 * editable after approval with no new round. Decided 30 September 2026.
 */
export const APPROVED_FIELDS = [
  'title',
  'audience',
  'channel',
  'format',
  'design_copy',
  'cta',
  'hashtags',
] as const

/** The keys of a patch that an approval covers, in the order they are named. */
export function approvedFieldsIn(patch: Record<string, unknown>): string[] {
  return APPROVED_FIELDS.filter((f) => f in patch)
}

/** "design copy and call to action", for the warning. */
export function fieldListLabel(fields: string[]): string {
  const names = fields.map((f) =>
    f === 'cta' ? 'call to action' : f === 'design_copy' ? 'design copy' : f.replace(/_/g, ' '),
  )
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/**
 * What may be done to a row, by whom. THE MIRROR OF THE STATE RULES IN
 * db/migrations/0410. Every refusal there has its button hidden or disabled
 * here, because a button the database then refuses teaches a person that the
 * system is unreliable, not that the thing is impossible.
 *
 *   Draft, Changes requested  everything, by the writer
 *   Submitted                 nothing, by anybody but the approver's decision
 *   Approved                  every field; one the approval covers only after a
 *                             warning, and the post goes back to Draft
 *   Published, Archived       nothing
 */
export function rowActions(
  row: Pick<ContentRow, 'state' | 'asset_count'>,
  caps: { canEditMarketing: boolean; canApproveContent: boolean },
) {
  const editable = caps.canEditMarketing
  const withAuthor = row.state === 'Draft' || row.state === 'Changes requested'
  const open = withAuthor || row.state === 'Approved'
  return {
    canEditCaptions: editable && open,
    // The writer's choice, never the approver's: an approver who picked the
    // caption would be approving their own choice.
    canChooseCaption: editable && open,
    canEditWording: editable && open,
    canEditNotes: editable && open,
    canEditSchedule: editable && open,
    // True when an edit to a covered field sends the post back to Draft.
    reopensOnEdit: editable && row.state === 'Approved',
    // No reopen path for files: a changed file is new artwork, and asking for it
    // through Send back keeps the reason on the record.
    canUpload: editable && withAuthor,
    canSubmit: editable && withAuthor,
    canDecide: caps.canApproveContent && row.state === 'Submitted',
    canPublish: editable && row.state === 'Approved',
    canArchive: editable && row.state !== 'Published' && row.state !== 'Archived',
  }
}

// ---------------------------------------------------------------------------
// Files on a design request: who may attach what
// ---------------------------------------------------------------------------
// THE MIRROR OF content_asset_record_v2 AND content_asset_delete_v1 IN
// db/migrations/0118. Every line below is a refusal written into one of those
// two functions. If the screen offers a button the database will refuse, the
// person does not learn that the thing is impossible, they learn that the system
// is unreliable, which is the standing reason this file holds these decisions as
// pure functions instead of scattering them through components.
//
// The distinction that matters: a REFERENCE is what the person asking sends in
// to show the designer what they mean, and ARTWORK is the work. Anyone who can
// raise a request can send in a reference on it, because requiring marketing
// access to attach the photograph that explains the request is what sent the
// photograph back to WhatsApp. Nobody delivers artwork against their own request
// unless they are the designer, because that would let a request be closed
// without a designer ever seeing it.

export type DesignAttachmentRights = {
  canAttachReference: boolean
  canRemoveReference: boolean
  canDeliverArtwork: boolean
  canRemoveArtwork: boolean
}

export function designAttachmentRights(
  row: Pick<DesignRequestRow, 'status' | 'requested_by' | 'assigned_to'>,
  who: { email: string | null; canEditMarketing: boolean; isDesigner: boolean },
): DesignAttachmentRights {
  // canEditMarketing already folds in admin, upstream in getMarketingRefs.
  const privileged = who.canEditMarketing || who.isDesigner
  const me = (who.email ?? '').trim().toLowerCase()
  const same = (other: string | null) =>
    me !== '' && (other ?? '').trim().toLowerCase() === me
  const mine = same(row.requested_by) || same(row.assigned_to)

  // Requested and In progress only. A delivered request is a record of what was
  // asked for and what came back, and a cancelled one is closed; sending more
  // into either changes a record after the fact.
  const open = row.status === 'Requested' || row.status === 'In progress'

  return {
    canAttachReference: open && (privileged || mine),
    canRemoveReference: open && (privileged || mine),
    canDeliverArtwork: privileged && row.status !== 'Cancelled',
    // Not once it is delivered. The file is what makes the request delivered,
    // and removing it would leave the board saying "Delivered, 0 files".
    canRemoveArtwork: privileged && row.status !== 'Delivered',
  }
}

/** Human-readable file size, or null when unknown. Same rules as documents. */
export function fileSize(bytes: string | number | null | undefined): string | null {
  const n = typeof bytes === 'string' ? Number(bytes) : bytes
  if (n == null || !Number.isFinite(n) || n <= 0) return null
  const units = ['B', 'KB', 'MB', 'GB']
  let value = n
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  const rounded = value >= 100 || unit === 0 ? Math.round(value) : Math.round(value * 10) / 10
  return `${rounded} ${units[unit]}`
}

/**
 * "12 Sep 2026", or the no-value marker for nothing.
 *
 * The glyph is the em dash used across this cockpit to mean "no value". That is
 * the one exception CLAUDE.md section 9 makes to the no-dashes rule, because it
 * is a data marker rather than punctuation and it predates the rule. It is
 * deliberate here, not an oversight to be tidied.
 */
export function shortDate(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })
}

/** "September 2026" for a plan's month. */
export function monthLabel(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })
}

/** The first of a month, as the value a date input and the API both want. */
export function firstOfMonth(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

/**
 * How long something has been waiting, in words.
 *
 * Says "today" for zero rather than "0 days", because "0 days waiting" reads as
 * a fault in the screen.
 */
export function waitedFor(days: number | null): string {
  if (days === null) return ''
  if (days <= 0) return 'today'
  if (days === 1) return '1 day'
  return `${days} days`
}

// ---------------------------------------------------------------------------
// Uploading a content plan (migration 0430)
// ---------------------------------------------------------------------------
// Every decision on the review screen is made in content_plan_import_review_v1
// and content_plan_import_commit_v1. What is here only shapes their answer for
// drawing: labels, tones, and the mix line in posts.

export type PlanFlag = {
  field: string
  value: string
  reason: string
  accepted_by: string | null
  accepted_at: string | null
}

export type PlanCaption = { label: string; body: string; hashtags: string[] }

export type PlanImportRow = {
  id: string
  post_no: number
  audience: string | null
  title: string | null
  channel: string
  format: string
  design_copy: string | null
  cta: string | null
  creative_direction: string | null
  source_basis: string | null
  production_note: string | null
  hashtags: string[] | null
  scheduled_for: string | null
  captions: PlanCaption[]
  flags: PlanFlag[]
  include: boolean
  live_id: string | null
  live_state: string | null
  live_ref: number | null
  // The day the live post already has. A document with no date for a post that
  // is on the calendar leaves it where it is, so the post is not "undated".
  live_scheduled_for: string | null
  action: 'new' | 'changed' | 'unchanged' | 'conflict' | 'archived'
  changed_fields: string[]
  open_flags: number
}

export type PlanFigure = { figure: string; context: string; accepted_by: string | null }

export type PlanReview = {
  import: {
    id: string
    status: 'Reading' | 'Pending Review' | 'Committed' | 'Discarded' | 'Failed'
    period_month: string | null
    files: { storage_path?: string; filename: string; size_bytes?: number; chars?: number }[]
    plan: { title?: string | null; targets?: { audience: string; target_share: number }[]; stated_mix?: string | null } | null
    figures_missing: PlanFigure[]
    remove_post_nos: number[]
    model: string | null
    cost_usd: number | null
    error: string | null
    created_by: string
    created_at: string
    committed_at: string | null
    commit_report: PlanCommitReport | null
  }
  existing_plan_id: string | null
  rows: PlanImportRow[]
  removed: { post_no: number; title: string; state: string; live_ref: number; confirmed: boolean }[]
  mix: { planned: Record<string, number>; target: { audience: string; target_share: number }[]; stated: string | null }
  undated: number
  open_flags: number
  open_figures: number
  blocked_reasons: string[]
}

export type PlanCommitReport = {
  plan_title: string
  posts_created: number
  posts_updated: number
  posts_unchanged: number
  posts_stopped: number
  caption_options_written: number
  calendar_days_filled: number
  undated: number
  conflicts: { post_no: number; title: string; state: string; live_ref: number; changed_fields: string[] }[]
  tasks_created: { ref: number; title: string }[]
}

export const PLAN_ACTION: Record<PlanImportRow['action'], { label: string; tone: 'green' | 'amber' | 'neutral' | 'red' }> = {
  new: { label: 'New', tone: 'green' },
  changed: { label: 'Changed', tone: 'amber' },
  unchanged: { label: 'Already there', tone: 'neutral' },
  conflict: { label: 'Conflict', tone: 'red' },
  archived: { label: 'Archived on the board', tone: 'neutral' },
}

/** "1 post", "2 posts". Every count on the plan screens goes through this,
 *  because "1 calendar days filled" is the sentence a reader stops at. */
export function countOf(n: number, one: string, many = one + 's'): string {
  return `${n} ${n === 1 ? one : many}`
}

/** "1 conflict", "2 conflicts". */
export function conflictCount(n: number): string {
  return countOf(n, 'conflict')
}

/** The line under "Committed to ..." on the report. */
export function commitReportLine(r: Pick<PlanCommitReport,
  'posts_created' | 'posts_updated' | 'posts_unchanged' | 'posts_stopped' | 'caption_options_written' | 'calendar_days_filled' | 'undated'>): string {
  const posts = [
    `${countOf(r.posts_created, 'post')} created`,
    `${r.posts_updated} updated`,
    `${r.posts_unchanged} already there`,
    ...(r.posts_stopped ? [`${r.posts_stopped} stopped`] : []),
  ].join(', ')
  const undated = r.undated
    ? `, ${countOf(r.undated, 'post')} still ${r.undated === 1 ? 'has' : 'have'} no date`
    : ''
  return `${posts}. ${countOf(r.caption_options_written, 'caption option')} written. `
    + `${countOf(r.calendar_days_filled, 'calendar day')} filled${undated}.`
}

/** "September 2026" once, not "September 2026, September 2026", when the plan
 *  is named after its month, which is what a reader does with no title. */
export function planHeading(title: string | null, month: string | null): string {
  const t = (title ?? '').trim()
  if (!month) return t ? `${t}, month not set` : 'Content plan, month not set'
  if (!t || t.toLowerCase() === month.toLowerCase()) return month
  return `${t}, ${month}`
}

/** The open (unaccepted) flag on a field, if any. */
export function openFlag(row: Pick<PlanImportRow, 'flags'>, field: string): PlanFlag | null {
  return row.flags.find((f) => f.field === field && !f.accepted_by) ?? null
}

/** "Driver 17 of 16 planned (85%, target 80%)". Posts first, because a
 *  one-post error is five points on a twenty-post feed and a percentage alone
 *  hides it. Every audience in either the target or the posts gets a line, so
 *  an audience the plan targets and never posts to is visible. */
export function planMixLines(mix: PlanReview['mix'], labels: Record<string, string> = {}) {
  const total = Object.values(mix.planned).reduce((s, n) => s + n, 0)
  const codes = [...new Set([...mix.target.map((t) => t.audience), ...Object.keys(mix.planned)])]
  return codes.map((code) => {
    const planned = mix.planned[code] ?? 0
    const target = mix.target.find((t) => t.audience === code)?.target_share ?? null
    const targetPosts = target === null ? null : Math.round((target / 100) * total)
    const pct = total === 0 ? 0 : Math.round((planned / total) * 100)
    return {
      code,
      label: labels[code] ?? code,
      planned,
      pct,
      target,
      targetPosts,
      off: targetPosts !== null && planned !== targetPosts,
    }
  })
}

/** Reads a File to base64 without the data: prefix. */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error(`${file.name} could not be read.`))
    reader.onload = () => {
      const s = String(reader.result ?? '')
      resolve(s.slice(s.indexOf(',') + 1))
    }
    reader.readAsDataURL(file)
  })
}
